import React, { useEffect, useState } from 'react';
import { Container, Typography, Paper, Box, Switch, FormControlLabel, Button, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import {
  getCookiePreferences,
  saveCookiePreferences,
  type CookiePreferences,
} from '../../utils/analytics';

const CookiesPage: React.FC = () => {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setPreferences(getCookiePreferences());
  }, []);

  const handleChange = (name: keyof CookiePreferences) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setPreferences({
      ...preferences,
      [name]: event.target.checked,
    });
  };

  const handleSave = () => {
    saveCookiePreferences(preferences);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <>
      <SEO
        title={t('cookies.title')}
        url="https://band-space.com/cookies/"
        canonical="https://band-space.com/cookies/"
      />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('cookies.title', 'Cookie Preferences')}
          </Typography>

          <Box sx={{ mt: 4 }}>
            <Typography variant="body1" paragraph>
              {t('cookies.introduction', 'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. Please select which cookies you are willing to store on your device.')}
            </Typography>

            {saved && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {t('cookies.preferencesSaved', 'Your preferences have been saved!')}
              </Alert>
            )}

            <Box sx={{ mt: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.necessary}
                    disabled
                    color="primary"
                  />
                }
                label={t('cookies.necessary', 'Necessary Cookies')}
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                {t('cookies.necessaryDescription', 'Required for the website to function properly. These cannot be disabled.')}
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.analytics}
                    onChange={handleChange('analytics')}
                    color="primary"
                  />
                }
                label={t('cookies.analytics', 'Analytics Cookies')}
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                {t('cookies.analyticsDescription', 'Help us understand how visitors interact with our website.')}
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.marketing}
                    onChange={handleChange('marketing')}
                    color="primary"
                  />
                }
                label={t('cookies.marketing', 'Marketing Cookies')}
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                {t('cookies.marketingDescription', 'Used to track visitors across websites to display relevant advertisements.')}
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.preferences}
                    onChange={handleChange('preferences')}
                    color="primary"
                  />
                }
                label={t('cookies.preferences', 'Preference Cookies')}
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                {t('cookies.preferencesDescription', 'Remember your settings and preferences for a better experience.')}
              </Typography>
            </Box>

            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
              >
                {t('cookies.savePreferences', 'Save Preferences')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setPreferences({
                  necessary: true,
                  analytics: false,
                  marketing: false,
                  preferences: false,
                })}
              >
                {t('cookies.rejectAll', 'Reject All')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default CookiesPage;
